import { useQuery } from '@tanstack/react-query'
import {
  getAllConsultationsByPatient,
  type ConsultationSummary,
} from '@/services/consultation.service'

/**
 * Single source of truth for a patient's progress notes (consultations).
 *
 * NOM-004 REQ-VIS-01 / REQ-FE-05: every view reads from this one cache (keyed
 * by patient); no view keeps its own copy. Surfaces derive signed-only /
 * drafts / latest from here — they never re-fetch or store separate versions.
 *
 * REQ-FE-01: fetches EVERY note (paged), never a silent subset; `total` exposes
 * the full signed count for display.
 */
const SIGNED_STATUSES = new Set(['completed', 'locked', 'unlock_requested'])

export function usePatientConsultations(patientId?: string) {
  const query = useQuery({
    queryKey: ['consultations-by-patient', patientId],
    queryFn: () => getAllConsultationsByPatient(patientId as string),
    enabled: !!patientId,
  })

  const all: ConsultationSummary[] = [...(query.data ?? [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )
  // Signed notes form the immutable evolution record; drafts are kept separate
  // so no view ever mixes them (REQ-VIS-03 / "no drafts in the signed list").
  const signed = all.filter((c) => SIGNED_STATUSES.has(c.status))
  const drafts = all.filter((c) => c.status === 'in_progress')

  return {
    ...query,
    all,
    signed,
    drafts,
    latestSigned: signed[0] ?? null,
    /** Full count of signed notes (REQ-FE-01 — shown, never hidden). */
    total: signed.length,
  }
}
